import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { goToIntake } from "@/lib/intake";
import { TREATMENTS } from "@/lib/treatments";

const NAV_ITEMS = [
  { label: "How It Works", to: "/#how-it-works" },
  { label: "Why Melbi", to: "/why-melbi" },
  { label: "FAQ", to: "/faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setCategoriesOpen(false);
    setMobileCategoriesOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCategoriesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const openDropdown = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setCategoriesOpen(true);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setCategoriesOpen(false), 120);
  };

  const isHashActive = (to) =>
    location.pathname === "/" && location.hash === to.replace("/", "");

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF9F6]/85 backdrop-blur-lg border-b border-[#E8E5DF]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {/* How It Works */}
            <NavLink
              to={NAV_ITEMS[0].to}
              data-testid={`nav-${NAV_ITEMS[0].label.toLowerCase().replace(/\s/g, "-")}`}
              className={() =>
                `text-sm font-medium transition-colors whitespace-nowrap ${
                  isHashActive(NAV_ITEMS[0].to)
                    ? "text-[#C96A4E]"
                    : "text-[#5E5C58] hover:text-[#2C2B29]"
                }`
              }
            >
              {NAV_ITEMS[0].label}
            </NavLink>

            {/* Treatments — dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={openDropdown}
              onMouseLeave={scheduleClose}
            >
              <button
                type="button"
                data-testid="nav-wellness-edit-dropdown-trigger"
                aria-expanded={categoriesOpen}
                aria-haspopup="true"
                onClick={() => setCategoriesOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-sm font-medium text-[#5E5C58] hover:text-[#2C2B29] transition-colors whitespace-nowrap"
              >
                Treatments
                <ChevronDown
                  size={14}
                  className={`transition-transform ${categoriesOpen ? "rotate-180" : ""}`}
                />
              </button>

              {categoriesOpen && (
                <div
                  data-testid="nav-wellness-edit-dropdown-panel"
                  onMouseEnter={openDropdown}
                  onMouseLeave={scheduleClose}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
                >
                  <div className="w-[520px] bg-white border border-[#E8E5DF] rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.08)] p-3 animate-fade-up">
                    <p className="px-3 pt-1 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
                      Choose a category to start
                    </p>
                    <ul className="grid grid-cols-2 gap-1">
                      {TREATMENTS.filter((t) => !t.hiddenFromNav).map((t) => (
                        <li key={t.slug}>
                          <Link
                            to={`/treatments/${t.slug}`}
                            data-testid={`nav-wellness-edit-item-${t.slug}`}
                            className="group flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F3F1EC] transition-colors"
                          >
                            <span
                              className="mt-1 h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: t.capColor || "#C96A4E" }}
                              aria-hidden="true"
                            />
                            <span className="flex-1 min-w-0">
                              <span className="block font-display text-[15px] font-semibold text-[#2C2B29] leading-tight group-hover:text-[#C96A4E] transition-colors">
                                {t.name}
                              </span>
                              <span className="block mt-0.5 text-[11px] font-medium uppercase tracking-[0.14em] text-[#8A9A92] truncate">
                                {t.navLabel || t.treatment}
                              </span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2 border-t border-[#E8E5DF] pt-2 px-3 pb-1 flex items-center justify-between">
                      <Link
                        to="/treatments"
                        data-testid="nav-wellness-edit-view-all"
                        className="text-xs font-semibold text-[#5E5C58] hover:text-[#2C2B29] transition-colors"
                      >
                        View all categories →
                      </Link>
                      <button
                        type="button"
                        onClick={goToIntake}
                        data-testid="nav-wellness-edit-start-now"
                        className="text-xs font-semibold text-[#C96A4E] hover:text-[#B35A40] transition-colors"
                      >
                        Choose your treatment →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Remaining nav items */}
            {NAV_ITEMS.slice(1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={() =>
                  `text-sm font-medium transition-colors whitespace-nowrap ${
                    isHashActive(item.to)
                      ? "text-[#C96A4E]"
                      : "text-[#5E5C58] hover:text-[#2C2B29]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={goToIntake}
              data-testid="nav-start-now-cta"
              className="inline-flex items-center bg-[#C96A4E] text-white rounded-full px-4 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
            >
              Start now
            </button>
            <a
              href="https://my.melbiwellness.com"
              data-testid="nav-login-cta"
              className="hidden sm:inline-flex items-center bg-white/80 backdrop-blur text-[#2C2B29] rounded-full px-4 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-semibold hover:bg-white border border-[#E8E5DF] transition-colors"
            >
              Log In
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-full hover:bg-[#F3F1EC] transition-colors"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#FAF9F6] border-t border-[#E8E5DF] animate-fade-up"
        >
          <nav className="px-4 py-4 flex flex-col gap-1">
            {/* How It Works */}
            <NavLink
              to={NAV_ITEMS[0].to}
              data-testid={`mobile-nav-${NAV_ITEMS[0].label.toLowerCase().replace(/\s/g, "-")}`}
              className="px-3 py-3 rounded-xl text-base font-medium transition-colors text-[#2C2B29] hover:bg-[#F3F1EC]"
            >
              {NAV_ITEMS[0].label}
            </NavLink>

            {/* Treatments — mobile accordion */}
            <div>
              <button
                type="button"
                data-testid="mobile-nav-wellness-edit-toggle"
                aria-expanded={mobileCategoriesOpen}
                onClick={() => setMobileCategoriesOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-base font-medium text-[#2C2B29] hover:bg-[#F3F1EC] transition-colors"
              >
                <span>Treatments</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${mobileCategoriesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileCategoriesOpen && (
                <ul
                  data-testid="mobile-nav-wellness-edit-panel"
                  className="mt-1 ml-2 pl-3 border-l border-[#E8E5DF] flex flex-col gap-0.5"
                >
                  {TREATMENTS.filter((t) => !t.hiddenFromNav).map((t) => (
                    <li key={t.slug}>
                      <Link
                        to={`/treatments/${t.slug}`}
                        data-testid={`mobile-nav-wellness-edit-item-${t.slug}`}
                        className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg hover:bg-[#F3F1EC] transition-colors"
                      >
                        <span
                          className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: t.capColor || "#C96A4E" }}
                          aria-hidden="true"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-[#2C2B29] leading-tight">
                            {t.name}
                          </span>
                          <span className="block mt-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-[#8A9A92]">
                            {t.navLabel || t.treatment}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link
                      to="/treatments"
                      data-testid="mobile-nav-wellness-edit-view-all"
                      className="block px-3 py-2 text-xs font-semibold text-[#5E5C58] hover:text-[#2C2B29] transition-colors"
                    >
                      View all categories →
                    </Link>
                  </li>
                </ul>
              )}
            </div>

            {/* Remaining nav items */}
            {NAV_ITEMS.slice(1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="px-3 py-3 rounded-xl text-base font-medium transition-colors text-[#2C2B29] hover:bg-[#F3F1EC]"
              >
                {item.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={goToIntake}
              data-testid="mobile-nav-start-now-cta"
              className="mt-3 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-[#B35A40] transition-colors"
            >
              Start now
            </button>
            <a
              href="https://my.melbiwellness.com"
              data-testid="mobile-nav-login-cta"
              className="inline-flex items-center justify-center bg-white text-[#2C2B29] rounded-full px-5 py-3 text-sm font-semibold border border-[#E8E5DF] hover:bg-[#F3F1EC] transition-colors"
            >
              Log In
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
